import React, { useCallback, useEffect, useState, useRef } from 'react'
import io from 'socket.io-client'
import { useFormik } from 'formik'
import { isEmpty, isNil } from 'ramda'
import queryString from 'query-string'
import { matchPath } from 'react-router'
import SearchIcon from '@material-ui/icons/Search'
import { withStyles } from '@material-ui/core/styles'
import { useIntl, FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import {
  Tab,
  Tabs,
  Link,
  Button,
  makeStyles,
  Typography,
  FormControl,
  InputAdornment,
} from '@material-ui/core'
import ListIcon from '@material-ui/icons/List'
import Circular from 'app/componentV4/Loader/Circular'
import InfiniteScroll from 'react-infinite-scroll-component'
import ViewDayOutlinedIcon from '@material-ui/icons/ViewDayOutlined'

import { useAccessToken } from 'app/auth/authHooks'

import { usePlace } from 'commons/usePlace'
import { SCOPE_BEACON } from 'app/constants/scope'
import { ACTION_NEW, GENERAL_DIALOG } from 'app/constants/compose'
import {
  CARD_VIEW,
  TABLE_VIEW,
  filterType,
  filterStatus,
  filterCreatBy,
  filterExpiration,
  DELETED_MSG,
} from 'app/constants/messageDetail'

import apiConfigs from 'app/configs/apiConfigs'
import { Routes } from 'app/services/routeService/routeService'
import {
  openDialog,
  updateFetchTimeline,
} from 'app/auth/store/actions/createNotification.actions'
import { useQueryClient } from '@tanstack/react-query'
import debounce from 'lodash.debounce'

import TabPanel from 'app/componentV4/Tabs/TabPanel'
import PanelPages from 'app/componentV4/Panels/Pages'
import PanelFilter from 'app/componentV4/Panels/Filter'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import Pagination from 'app/componentV4/pagination/Pagination'
import CalendarSelector from 'app/componentV4/Filters/CalendarSelector'
import CheckboxSelectorV2 from 'app/componentV4/Filters/CheckboxSelectorV2'

import CardView from 'app/main/messages/timeline/CardView'
import TableView from 'app/main/messages/timeline/TableView'
import ViewDetails from 'app/main/messages/timeline/ViewDetails'
import useTimeline from 'app/main/messages/timeline/useTimeline'
import EmptyMessagePage from 'app/main/messages/timeline/EmptyMessagePage'
import ComposeMessageButton from 'app/componentV4/Button/ComposeMessageButton'

const CustomsTabs = withStyles({
  root: {
    minHeight: 'unset',
  },
})(Tabs)

const useStyles = makeStyles((theme) => ({
  tab: {
    '& .MuiTab-root': {
      width: 'fit-content',
      maxWidth: 'unset',
      minWidth: 'unset',
      height: 'fit-content',
      minHeight: 'unset',
      padding: '7px',
      color: theme.colors['grey-100'],
    },
    '& .MuiTabs-flexContainer': {
      backgroundColor: theme.colors['neutrals-100'],
      padding: '2px',
      borderRadius: '4px',
    },
    '& .MuiTab-textColorInherit.Mui-selected': {
      color: '#FFFFFF',
      backgroundColor: theme.colors['secondary'],
      borderRadius: '3px',
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  infiniteScroll: {
    height: 'fit-content',
    maxHeight: 'calc(100vh - 224px)',
    overflow: 'auto',
  },
}))

const MessageTimeline = ({ history, location }) => {
  const isFavoritePath = location.pathname === Routes.notificationFavolites()
  const place = usePlace()
  const classes = useStyles()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const accessToken = useAccessToken()
  const parsed = queryString.parse(location.search, { decode: false })
  const tabView = Number(localStorage.getItem('timelineView')) ?? 0
  const {
    state: {
      params,
      isHasMore,
      isUpdating,
      notifications,
      isInfiniteScroll,
      notificationDetail,
      isEmptyNotification,
      infiniteNotifications,
      isLoadingNotifications,
      isLoadingNotificationDetail,
      isFavoriteLoading,
    },
    handleSort,
    handlePages,
    handleSearch,
    handleFilter,
    handleUpdating,
    handleDateFilter,
    handleChangeTabs,
    handleTypeFilter,
    handleResetFilter,
    handleFetchUpdateSocket,
    handleFetchNotifications,
    handleFetchNotificationsById,
    handleUpdateTimelineCardView,
    handleClearNotificationDetail,
    handleUpdateDetailAndTimelineTableView,
    updateFavorites,
  } = useTimeline(isFavoritePath)

  const { data, meta } = notifications

  const scrollRef = useRef(null)
  const [id, setId] = useState(null)
  const [updateId, setUpdateId] = useState(null)

  const [isOpenMetrics, setIsMetrics] = useState(false)
  const [isOpenComment, setIsOpenComment] = useState(false)
  const [isOpenDetail, setIsOpenDetail] = useState(false)
  const isOpenNotificationDialog = useSelector(
    ({ auth }) => auth.createNotification.isOpen,
  )
  const queryClient = useQueryClient()
  const formik = useFormik({
    initialValues: {
      search: '',
      filterDate: null,
      filterType: [],
      filterExpiration: [],
      filterStatus: [],
      filterCreatBy: [],
    },
  })
  const handleResetTimeline = useCallback(() => {
    handleResetFilter(tabView)
    formik.resetForm()
  }, [formik, handleResetFilter, tabView])

  const fetchFirstNotificationList = useCallback(() => {
    dispatch(
      updateFetchTimeline({
        onCreateSuccess: handleResetTimeline,
      }),
    )
    handleFetchNotifications(params, tabView, true)
  }, [dispatch, handleFetchNotifications, handleResetTimeline, params, tabView])

  const handleOpenMetrics = (id) => {
    setId(id)
    setIsOpenDetail(true)
    setIsMetrics(true)
  }

  const handleOpenDetail = (id) => {
    setId(id)
    handleFetchNotificationsById(id)
    setIsOpenDetail(true)
    setIsMetrics(false)
  }

  const handleCloseDetail = () => {
    setId(null)
    setIsOpenDetail(false)
    setIsMetrics(false)
    setIsOpenComment(false)
    handleClearNotificationDetail()
  }

  const handleChangeState = (event, newValue) => {
    localStorage.setItem('timelineView', newValue.toString())
    handleChangeTabs(newValue) // When tab onChange always reset page and keep filter value
  }

  const redirectPage = useCallback((res, scope) => {
    return scope === SCOPE_BEACON
      ? Routes.beaconNotification()
      : Routes.notificationTimeline()
  }, [])

  const onSuccseAndRedirectPage = useCallback(
    (res, scope) => {
      const pathname = redirectPage(scope)

      const match = matchPath(history.location.pathname, {
        path: pathname,
      })

      if (isNil(match)) {
        history.push(pathname)
      } else {
        handleResetTimeline()
      }
    },
    [handleResetTimeline, history, redirectPage],
  )

  const onOpenCreateNotificationDialog = () => {
    dispatch(
      openDialog({
        initialValues: undefined,
        onCreateSuccess: onSuccseAndRedirectPage,
        type: GENERAL_DIALOG,
        action: ACTION_NEW,
      }),
    )
  }

  const handleOpenCompose = useCallback(() => {
    dispatch(
      openDialog({
        initialValues: undefined,
        onCreateSuccess: (res, scope) => {
          const pathname = redirectPage(res, scope)
          history.push(pathname)
          handleResetTimeline()
        },
        type: GENERAL_DIALOG,
        action: ACTION_NEW,
      }),
    )
  }, [dispatch, handleResetTimeline, history, redirectPage])

  const getNewParams = () => {
    if (
      meta.lastPage === meta.currentPage &&
      data.length === 1 &&
      meta.lastPage > 1
    ) {
      return { ...params, page: meta.lastPage - 1 }
    }
    return params
  }

  const handleFetchTimeline = async (id, status) => {
    if (tabView === TABLE_VIEW) {
      const newParams = status === DELETED_MSG ? getNewParams() : params
      handleFetchNotifications(newParams, tabView)
    } else if (tabView === CARD_VIEW) {
      setUpdateId(id)
      handleUpdating(true)
      handleUpdateTimelineCardView(id, status)
    } else return
  }

  // Load without Loader
  const handleFetchDetailAndTimeline = async (id, status, obj) => {
    if (tabView === TABLE_VIEW)
      handleUpdateDetailAndTimelineTableView(id, params, tabView)
    else if (tabView === CARD_VIEW)
      handleUpdateTimelineCardView(id, status, obj)
    else return
  }

  const handleOpenComments = (id) => {
    handleOpenDetail(id)
    setIsOpenComment(true)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchFirstNotificationList, [])

  const handleDeliveryStatusUpdated = useCallback(
    debounce((data) => {
      if (!data || !data.id) return
      queryClient.invalidateQueries({
        queryKey: ['message', data.id, 'metrics'],
      })
    }, 1000),
    [queryClient],
  )

  useEffect(() => {
    const socket = io(`${apiConfigs.apiPrefixURL}/auth`, {
      query: `placeId=${place.id}`,
      auth: {
        accessToken,
      },
      transports: ['websocket', 'polling'],
    })
    socket.on(`message translation completed`, async (data) => {
      if (isEmpty(data)) return
      else handleFetchUpdateSocket(data, null, tabView)
    })
    socket.on(`message delivery status updated`, async (data) => {
      if (isEmpty(data)) return
      handleDeliveryStatusUpdated(data)
    })
    return () => {
      socket.disconnect()
    }
  }, [
    accessToken,
    handleFetchUpdateSocket,
    handleDeliveryStatusUpdated,
    place.id,
    tabView,
  ])
  useEffect(() => {
    if (isLoadingNotifications) {
      scrollRef.current.scrollTo(0, 0)
    }
  }, [isLoadingNotifications])

  useEffect(() => {
    if (parsed.on_compose === 'true') {
      handleOpenCompose()
      delete parsed.on_compose
      history.replace({
        pathname: location.pathname,
        search: queryString.stringify(parsed),
      })
    }
  }, [handleOpenCompose, history, location.pathname, parsed])

  return (
    <PanelPages
      isEmptyState={isEmptyNotification}
      emptyState={
        <EmptyMessagePage
          isFavoritePath={isFavoritePath}
          onOpenCreateNotification={onOpenCreateNotificationDialog}
        />
      }
      title={formatMessage({
        id: isFavoritePath
          ? 'navigation.favorites'
          : 'navigation.profile.notificationTimeline.label',
      })}
      rightContent={
        <div className="space-y-5 md:space-y-0 md:space-x-10 md:flex items-center">
          {/* Search */}
          <FormControl variant="outlined" className="w-full md:w-256">
            <SimpleInput
              id="search-timeline-by-title"
              name="search"
              placeholder={formatMessage({
                id: 'timeline.serach.message',
              })}
              value={formik.values.search}
              size="small"
              fullWidth
              onChange={(event) => {
                formik.setFieldValue('search', event.target.value)
                handleSearch(event.target.value, tabView)
              }}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon className="text-grey-400" />
                </InputAdornment>
              }
            />
          </FormControl>

          {/* Compose button */}
          <ComposeMessageButton onClick={onOpenCreateNotificationDialog} />
        </div>
      }
    >
      {/* Filter */}
      <PanelFilter
        leftContent={
          <>
            <CalendarSelector
              name="filterDate"
              values={formik.values.filterDate}
              className="mr-5 mt-5"
              label={formatMessage({ id: 'text.dates' })}
              onChange={(value) => {
                formik.setFieldValue('filterDate', value)
                handleDateFilter(value, tabView)
              }}
              disabled={isLoadingNotifications}
            />
            <CheckboxSelectorV2
              name="filterType"
              label={formatMessage({ id: 'text.type' })}
              options={filterType}
              value={formik.values.filterType}
              disabled={isLoadingNotifications}
              onChange={(value) => {
                formik.setFieldValue('filterType', value)
                handleTypeFilter(value, tabView)
              }}
              className="mr-5 bg-primary-200 text-primary text-14 font-normal"
            />
            <CheckboxSelectorV2
              name="filterExpiration"
              label={formatMessage({ id: 'text.expiration' })}
              options={filterExpiration}
              value={formik.values.filterExpiration}
              disabled={isLoadingNotifications}
              onChange={(value) => {
                formik.setFieldValue('filterExpiration', value)
                handleFilter(value, 'filterExpiration', tabView)
              }}
              className="mr-5 bg-primary-200 text-primary text-14 font-normal"
            />
            <CheckboxSelectorV2
              name="filterStatus"
              label={formatMessage({ id: 'text.status' })}
              options={filterStatus}
              value={formik.values.filterStatus}
              disabled={isLoadingNotifications}
              onChange={(value) => {
                formik.setFieldValue('filterStatus', value)
                handleFilter(value, 'filterStatus', tabView)
              }}
              className="mr-5 bg-primary-200 text-primary text-14 font-normal"
            />
            <CheckboxSelectorV2
              name="filterCreatBy"
              label={formatMessage({ id: 'text.create.by' })}
              isOneSelected={true}
              options={filterCreatBy}
              value={formik.values.filterCreatBy}
              disabled={isLoadingNotifications}
              onChange={(value) => {
                formik.setFieldValue('filterCreatBy', value)
                handleFilter(value, 'filterCreatBy', tabView)
              }}
              className="mr-5 bg-primary-200 text-primary text-14 font-normal"
            />
            {formik.dirty ? (
              <Button
                className="p-0 text-primary text-14 font-bold normal-case disabled:text-grey-200"
                onClick={handleResetTimeline}
                disabled={isLoadingNotifications}
              >
                {formatMessage({ id: 'text.clearAll' })}
              </Button>
            ) : null}
          </>
        }
        rightContent={
          <CustomsTabs
            value={tabView}
            onChange={handleChangeState}
            aria-label="Message Timeline View Tabs"
            className={classes.tab}
          >
            <Tab
              icon={<ListIcon style={{ fontSize: 15 }} />}
              id="message-timeline-table-view"
              aria-controls="message-timeline-table-view"
            />
            <Tab
              icon={<ViewDayOutlinedIcon style={{ fontSize: 15 }} />}
              id="message-timeline-card-view"
              aria-controls="message-timeline-card-view"
            />
          </CustomsTabs>
        }
      />
      {/* Table */}
      <TabPanel name="timeline-table-view" value={tabView} index={TABLE_VIEW}>
        <TableView
          notifications={data}
          onOpenDetail={handleOpenDetail}
          onOpenMetric={handleOpenMetrics}
          onOpenComment={handleOpenComments}
          isLoading={isLoadingNotifications && tabView === TABLE_VIEW}
          isOpenDetail={isOpenDetail || isOpenNotificationDialog}
          onSortTable={(value) => handleSort(value, tabView)}
          handleFetchTimeline={(id, status) => handleFetchTimeline(id, status)}
          onSuccseAndRedirectPage={onSuccseAndRedirectPage}
          isFavoriteLoading={isFavoriteLoading}
          updateFavorites={(id, isFavorite) =>
            updateFavorites(id, isFavorite, tabView)
          }
        />
        {!isEmpty(meta) && tabView === TABLE_VIEW ? (
          <Pagination
            isPerpage={false}
            totalCount={meta.total}
            pageSize={meta.perPage}
            dataLength={data.length}
            lastPage={Number(meta.lastPage)}
            currentPage={meta.currentPage}
            onPageChange={(page) => handlePages(page, tabView)}
          />
        ) : null}
      </TabPanel>
      {/* Card */}
      <TabPanel name="timeline-table-card" value={tabView} index={CARD_VIEW}>
        <div
          id="scrollTimelineCardView"
          className={classes.infiniteScroll}
          ref={scrollRef}
        >
          <InfiniteScroll
            className="w-full overflow-x-hidden"
            dataLength={infiniteNotifications.length}
            next={() =>
              !isLoadingNotifications &&
              handlePages(++params.page, tabView, true)
            }
            hasMore={isHasMore}
            endMessage={
              !isHasMore && !isEmpty(infiniteNotifications) ? (
                <div className="w-full text-center space-y-10 mt-10">
                  <Typography className="text-16 font-normal text-primary-300">
                    <FormattedMessage id="notificationTimeline.endTimeline" />
                  </Typography>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => scrollRef.current.scrollTo(0, 0)}
                  >
                    <FormattedMessage id="text.backToTop" />
                  </Link>
                </div>
              ) : null
            }
            scrollableTarget="scrollTimelineCardView"
            loader={
              <div className="flex justify-center py-64">
                {isInfiniteScroll ? (
                  <Circular size={32} className="my-10 text-black" />
                ) : null}
              </div>
            }
          >
            <CardView
              updateId={updateId}
              isUpdating={isUpdating}
              onOpenDetail={handleOpenDetail}
              onOpenMetric={handleOpenMetrics}
              onOpenComment={handleOpenComments}
              isLoading={isLoadingNotifications && tabView === CARD_VIEW}
              notifications={infiniteNotifications}
              onSuccseAndRedirectPage={onSuccseAndRedirectPage}
              handleFetchTimeline={handleFetchTimeline}
              isOpenDetail={isOpenDetail || isOpenNotificationDialog}
              isFavoriteLoading={isFavoriteLoading}
              updateFavorites={(id, isFavorite) =>
                updateFavorites(id, isFavorite, tabView)
              }
            />
          </InfiniteScroll>
        </div>
      </TabPanel>
      {isOpenDetail ? (
        <ViewDetails
          id={id}
          isOpen={isOpenDetail}
          isComment={isOpenComment}
          isMetrics={isOpenMetrics}
          value={notificationDetail}
          onClose={handleCloseDetail}
          onFetch={handleFetchDetailAndTimeline}
          isLoading={isLoadingNotificationDetail}
          handleFetchTimeline={(id, status) => handleFetchTimeline(id, status)}
          onSuccseAndRedirectPage={(res, scope) => {
            onSuccseAndRedirectPage(res, scope)
            handleCloseDetail()
          }}
          onFetchDetail={(id, languageId) =>
            handleFetchNotificationsById(id, languageId)
          }
          isFavoriteLoading={isFavoriteLoading}
          updateFavorites={(id, isFavorite) =>
            updateFavorites(id, isFavorite, tabView)
          }
        />
      ) : null}
    </PanelPages>
  )
}

export default MessageTimeline
